<template>
  <div class="rc">
    <div class="rc__page">
      <header class="rc__header">
        <div class="rc__heading">
          <a v-if="page.hikeUrl" :href="page.hikeUrl" class="rc__round-button"
             :aria-label="t('route_cards.back_to_hike', { name: page.hikeName })">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"
                 stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7"></path></svg>
          </a>
          <div class="rc__titles">
            <div class="rc__title-row">
              <h1 class="rc__title">{{ t('route_cards.title') }}</h1>
              <span class="rc__pro">{{ t('shared.pro') }}</span>
              <a :href="ROUTE_CARD_ARTICLE_URL" target="_blank" rel="noopener" class="rc__help"
                 :aria-label="t('route_cards.help')" :title="t('route_cards.help')">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                     stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="9"></circle><path d="M12 11v5"></path><path d="M12 7.5h.01"></path>
                </svg>
              </a>
            </div>
            <div class="rc__subtitle">{{ subtitle }}</div>
          </div>
        </div>

        <div v-if="card && !card.locked" class="rc__actions">
          <template v-if="editing">
            <button v-if="!card.shared" type="button" class="rc__pill" :disabled="busy" @click="shareLink">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
                   stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M10 14a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1"></path>
                <path d="M14 10a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1"></path>
              </svg>
              {{ t('route_cards.share_link') }}
            </button>
            <template v-else>
              <button type="button" class="rc__pill" :disabled="busy" @click="copyShareLink">
                {{ copied ? t('route_cards.link_copied') : t('route_cards.copy_link') }}
              </button>
              <button type="button" class="rc__pill" :disabled="busy" @click="stopSharing">
                {{ t('route_cards.stop_sharing') }}
              </button>
            </template>
          </template>
          <button type="button" class="rc__pill rc__pill--primary" :disabled="busy" @click="downloadPdf">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"
                 stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M12 4v11"></path><path d="M7 10l5 5 5-5"></path><path d="M5 20h14"></path>
            </svg>
            {{ t('route_cards.download_pdf') }}
          </button>
        </div>
      </header>

      <label v-if="editing && card?.shared && card.shareUrl" class="rc__share-url">
        <span>{{ t('route_cards.footnote_shared') }}</span>
        <input type="text" readonly :value="card.shareUrl" @focus="($event.target as HTMLInputElement).select()">
      </label>

      <p v-if="signedOut" class="rc__message">
        {{ t('route_cards.sign_in_prompt') }}
        <a href="/login" class="rc__link">{{ t('route_cards.sign_in') }}</a>
      </p>
      <p v-else-if="loadError" class="rc__message" role="alert">{{ loadError }}</p>
      <p v-else-if="!card" class="rc__message">{{ t('route_cards.loading') }}</p>
      <p v-else-if="page.route.length < 2" class="rc__message">{{ t('route_cards.no_route') }}</p>

      <template v-else>
        <div v-if="editing" class="rc__settings">
          <label v-if="page.days.length > 1" class="rc__field">
            <span>{{ t('route_cards.settings.day') }}</span>
            <select :value="page.hikeDayId" @change="switchDay(($event.target as HTMLSelectElement).value)">
              <option v-for="day in page.days" :key="day.id" :value="day.id">{{ day.name }}</option>
            </select>
          </label>
          <label class="rc__field">
            <span>{{ t('route_cards.settings.date') }}</span>
            <input type="date" :value="settings.startDate ?? ''" @change="setDate(($event.target as HTMLInputElement).value)">
          </label>
          <label class="rc__field">
            <span>{{ t('route_cards.settings.start') }}</span>
            <input type="time" :value="formatClock(settings.startMinutes)"
                   @change="setStart(($event.target as HTMLInputElement).value)">
          </label>
          <label class="rc__field rc__field--accent">
            <span>{{ t('route_cards.settings.pace') }}</span>
            <select :value="settings.paceKmh" @change="setSetting('paceKmh', Number(($event.target as HTMLSelectElement).value))">
              <option v-for="pace in PACE_OPTIONS" :key="pace" :value="pace">
                {{ t('route_cards.pace_value', { pace }) }}
              </option>
            </select>
          </label>
          <label class="rc__field">
            <span>{{ t('route_cards.settings.climb') }}</span>
            <select :value="settings.climbMinutesPer100m"
                    @change="setSetting('climbMinutesPer100m', Number(($event.target as HTMLSelectElement).value))">
              <option v-for="climb in CLIMB_OPTIONS" :key="climb" :value="climb">
                {{ t('route_cards.climb_value', { minutes: climb }) }}
              </option>
            </select>
          </label>
          <label class="rc__field">
            <span>{{ t('route_cards.settings.grid') }}</span>
            <select :value="settings.gridCrs ?? ''" @change="setGrid(($event.target as HTMLSelectElement).value)">
              <option value="">{{ t('route_cards.grid_default', { name: card.gridName }) }}</option>
              <option value="ie">{{ t('route_cards.grids.ie') }}</option>
              <option value="gb">{{ t('route_cards.grids.gb') }}</option>
              <option value="utm">{{ t('route_cards.grids.utm') }}</option>
              <option value="nz">{{ t('route_cards.grids.nz') }}</option>
              <option value="latlon">{{ t('route_cards.grids.latlon') }}</option>
              <option value="latlon_dms">{{ t('route_cards.grids.latlon_dms') }}</option>
            </select>
          </label>
          <label class="rc__field rc__field--narrow">
            <span>{{ t('route_cards.settings.party') }}</span>
            <input type="number" min="1" max="99" inputmode="numeric" :value="settings.partySize ?? ''"
                   @change="setSetting('partySize', partySizeFrom(($event.target as HTMLInputElement).value))">
          </label>
          <label class="rc__field rc__field--wide">
            <span>{{ t('route_cards.settings.party_names') }}</span>
            <input type="text" maxlength="200" :value="settings.partyNames"
                   @change="setSetting('partyNames', ($event.target as HTMLInputElement).value)">
          </label>
          <label class="rc__field">
            <span>{{ t('route_cards.settings.leader_mobile') }}</span>
            <input type="tel" maxlength="40" :value="settings.leaderMobile"
                   @change="setSetting('leaderMobile', ($event.target as HTMLInputElement).value)">
          </label>
        </div>

        <div class="rc__body">
          <section class="rc__map-panel">
            <div class="rc__panel-head">
              <h2 class="rc__panel-title">{{ t('route_cards.points_and_sections') }}</h2>
              <div v-if="editing" class="rc__panel-actions">
                <label class="rc__inline-select">
                  <span class="visually-hidden">{{ t('route_cards.start_from') }}</span>
                  <select :disabled="busy" @change="startFrom(($event.target as HTMLSelectElement))">
                    <option value="">{{ t('route_cards.start_from') }}</option>
                    <option value="SUGGESTED">{{ t('route_cards.start_from_suggested') }}</option>
                    <option value="PLANNER">{{ t('route_cards.start_from_planner') }}</option>
                  </select>
                </label>
                <button type="button" class="rc__small-button" @click="addPoint">{{ t('route_cards.add_point') }}</button>
              </div>
            </div>
            <RouteCardMap :route="page.route" :points="points" :selected="selected" :editable="editing"
                          @select="selected = $event" @move="movePointTo" />
            <p class="rc__help">{{ editing ? t('route_cards.map_help') : t('route_cards.map_help_read_only') }}</p>
          </section>

          <section class="rc__card-panel">
            <RouteCardTable :card="card" :selected="selected" @select="selected = $event">
              <template v-if="editing" #editor="{ pointIndex }">
                <div class="rc__editor">
                  <label class="rc__editor-field">
                    <span>{{ t('route_cards.edit.name') }}</span>
                    <input type="text" :maxlength="80" :value="points[pointIndex]?.name"
                           :placeholder="t('route_cards.point_fallback', { number: pointIndex })"
                           @change="patchPoint(pointIndex, { name: ($event.target as HTMLInputElement).value })">
                  </label>
                  <label class="rc__editor-field">
                    <span>{{ t('route_cards.edit.note') }}</span>
                    <input type="text" :maxlength="500" :value="points[pointIndex]?.note"
                           @change="patchPoint(pointIndex, { note: ($event.target as HTMLInputElement).value })">
                  </label>
                  <label class="rc__editor-field">
                    <span>{{ t('route_cards.edit.escape') }}</span>
                    <input type="text" :maxlength="500" :value="points[pointIndex]?.escape"
                           @change="patchPoint(pointIndex, { escape: ($event.target as HTMLInputElement).value })">
                  </label>
                  <label v-if="pointIndex < points.length - 1" class="rc__editor-field rc__editor-field--narrow">
                    <span>{{ t('route_cards.edit.rest') }}</span>
                    <select :value="points[pointIndex]?.restMinutes"
                            @change="patchPoint(pointIndex, { restMinutes: Number(($event.target as HTMLSelectElement).value) })">
                      <option v-for="rest in REST_OPTIONS" :key="rest" :value="rest">
                        {{ rest === 0 ? t('route_cards.edit.no_rest') : t('route_cards.edit.rest_minutes', { minutes: rest }) }}
                      </option>
                    </select>
                  </label>
                  <button v-if="pointIndex < points.length - 1" type="button" class="rc__small-button rc__small-button--danger"
                          @click="removeSelected(pointIndex)">
                    {{ t('route_cards.edit.remove_point') }}
                  </button>
                </div>
              </template>
            </RouteCardTable>

            <div v-if="card.locked" class="rc__paywall">
              <div class="rc__paywall-blur" aria-hidden="true">
                <div></div><div></div><div></div>
              </div>
              <div class="rc__paywall-card">
                <span class="rc__pro">{{ t('shared.pro') }}</span>
                <h2>{{ t('route_cards.paywall.title') }}</h2>
                <p>{{ t('route_cards.paywall.more_sections', { count: card.legCount - card.legs.length }, card.legCount - card.legs.length) }}</p>
                <ul>
                  <li>{{ t('route_cards.paywall.grid') }}</li>
                  <li>{{ t('route_cards.paywall.timings') }}</li>
                  <li>{{ t('route_cards.paywall.print') }}</li>
                </ul>
                <a href="/purchases" class="rc__pill rc__pill--primary">{{ t('route_cards.paywall.upgrade') }}</a>
              </div>
            </div>

            <div v-if="card.totals" class="rc__totals">
              <div class="rc__tile">
                <span>{{ t('route_cards.totals.distance') }}</span>
                <strong>{{ t('route_cards.km_value', { value: formatKm(card.totals.distanceMeters) }) }}</strong>
              </div>
              <div class="rc__tile">
                <span>{{ t('route_cards.totals.ascent_descent') }}</span>
                <strong>↑{{ card.totals.ascentMeters }} ↓{{ card.totals.descentMeters }} m</strong>
              </div>
              <div class="rc__tile">
                <span>{{ t('route_cards.totals.moving_rests') }}</span>
                <strong>{{ formatDuration(card.totals.movingMinutes) }} + {{ formatDuration(card.totals.restMinutes) }}</strong>
              </div>
              <div class="rc__tile">
                <span>{{ t('route_cards.totals.finish') }}</span>
                <strong class="rc__yellow">{{ formatClock(card.totals.finishMinutes) }}</strong>
              </div>
              <div v-if="card.sunset && card.totals.spareDaylightMinutes !== null" class="rc__tile">
                <span>{{ t('route_cards.totals.sunset', { time: card.sunset }) }}</span>
                <strong :class="card.totals.spareDaylightMinutes >= 0 ? 'rc__green' : 'rc__orange'">
                  {{ card.totals.spareDaylightMinutes >= 0
                    ? t('route_cards.totals.spare', { time: formatHoursMinutes(card.totals.spareDaylightMinutes) })
                    : t('route_cards.totals.after_dark', { time: formatHoursMinutes(-card.totals.spareDaylightMinutes) }) }}
                </strong>
              </div>
            </div>

            <p class="rc__footnote">
              <span v-if="card.gridToMagneticDegrees !== null">
                {{ t('route_cards.footnote_magnetic', { offset: formatOffset(card.gridToMagneticDegrees), grid: card.gridName }) }}
              </span>
              {{ t('route_cards.footnote_naismith') }}
              <span v-if="editing">{{ card.shared ? t('route_cards.footnote_shared') : t('route_cards.footnote_private') }}</span>
            </p>
            <p v-if="saveState" class="rc__status" :class="{ 'rc__status--error': saveState === 'error' }" role="status">
              {{ saveState === 'saving' ? t('route_cards.saving') : saveState === 'saved' ? t('route_cards.saved') : t('route_cards.save_failed') }}
            </p>
          </section>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import graphClient from '../../utils/graphql/graph_client';
import {
  GetRouteCard, GetSharedRouteCard, SaveRouteCard, SetRouteCardSharing,
} from '../../utils/graphql/route-cards-graph';
import {
  addPointInSection, fromCardPoints, movePoint, removePoint, REST_OPTIONS, updatePoint, type EditableCheckpoint,
} from '../../utils/route_cards/checkpoints';
import { buildSaveInput, partySizeFrom, settingsFromCard, type EditableSettings } from '../../utils/route_cards/cardInput';
import {
  formatClock, formatDuration, formatHoursMinutes, formatKm, formatOffset, parseClock,
} from '../../utils/route_cards/format';
import { CLIMB_OPTIONS, PACE_OPTIONS } from '../../utils/route_cards/settings';
import { ROUTE_CARD_ARTICLE_URL } from '../../utils/route_cards/supportArticle';
import {
  ROUTE_CARD_CREATED, ROUTE_CARD_PAYWALL_SHOWN, ROUTE_CARD_PDF_EXPORTED, trackRouteCard,
} from '../../utils/route_cards/analytics';
import RouteCardMap from './RouteCardMap.vue';
import RouteCardTable from './RouteCardTable.vue';
import type { RouteCard, RouteCardPage } from './types';

// The route card page. In the editor every change is saved straight away —
// a short debounce, then the whole card goes up and the recalculated card comes
// back — so there is no Save button and nothing to lose. The server does all
// the arithmetic; this page only moves points and formats what comes back.
const props = defineProps<{ page: RouteCardPage }>();
const { t } = useI18n();

const SAVE_DEBOUNCE_MS = 600;

const card = ref<RouteCard | null>(null);
const points = ref<EditableCheckpoint[]>([]);
const settings = reactive<EditableSettings>({
  paceKmh: 4, climbMinutesPer100m: 10, startDate: null, startMinutes: 540, gridCrs: null,
  partySize: null, partyNames: '', leaderMobile: '',
});
const selected = ref(1);
const signedOut = ref(false);
const loadError = ref<string | null>(null);
const busy = ref(false);
const copied = ref(false);
const saveState = ref<'saving' | 'saved' | 'error' | null>(null);

let saveTimer: ReturnType<typeof setTimeout> | null = null;
// Bumped on every local edit, so a save that comes back after a newer edit
// doesn't overwrite the points the user has moved since.
let revision = 0;
let paywallTracked = false;

const editing = computed(() => props.page.mode === 'edit' && !!card.value && !card.value.locked);

const subtitle = computed(() => {
  const name = card.value?.hikeName ?? props.page.hikeName ?? '';
  const day = card.value?.dayName;
  return day && props.page.days.length !== 1 ? `${name} · ${day}` : name;
});

const surface = computed(() => (props.page.mode === 'shared' ? 'web_shared' : 'web_editor'));

function eventContext() {
  return { hikeDayId: props.page.hikeDayId, legCount: card.value?.legCount ?? 0, surface: surface.value } as const;
}

function errorCode(error: unknown): string | undefined {
  const response = (error as { response?: { errors?: Array<{ message?: string; extensions?: { code?: string } }> } })
    ?.response;
  return response?.errors?.[0]?.extensions?.code ?? response?.errors?.[0]?.message;
}

function apply(next: RouteCard, { keepLocalPoints = false } = {}): void {
  card.value = next;
  if (!keepLocalPoints) points.value = fromCardPoints(next.points);
  Object.assign(settings, settingsFromCard(next));
  selected.value = Math.min(Math.max(selected.value, 1), Math.max(next.points.length - 1, 1));

  if (next.locked && !paywallTracked) {
    paywallTracked = true;
    trackRouteCard(ROUTE_CARD_PAYWALL_SHOWN, eventContext());
  }
}

async function load(): Promise<void> {
  try {
    if (props.page.mode === 'shared') {
      const data = await graphClient.request<{ sharedRouteCard: RouteCard | null }>(
        GetSharedRouteCard, { token: props.page.shareToken },
      );
      if (data.sharedRouteCard) apply(data.sharedRouteCard); else loadError.value = t('route_cards.load_failed');
      return;
    }
    const data = await graphClient.request<{ routeCard: RouteCard }>(GetRouteCard, { hikeDayId: props.page.hikeDayId });
    apply(data.routeCard);
  } catch (error) {
    if (errorCode(error) === 'Unauthenticated') signedOut.value = true;
    else loadError.value = t('route_cards.load_failed');
  }
}

async function saveNow(): Promise<RouteCard | null> {
  if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; }
  const sentRevision = revision;
  const wasNew = !card.value?.id;
  saveState.value = 'saving';
  try {
    const data = await graphClient.request<{ saveRouteCard: RouteCard }>(SaveRouteCard, {
      input: buildSaveInput(props.page.hikeDayId, settings, points.value),
    });
    apply(data.saveRouteCard, { keepLocalPoints: sentRevision !== revision });
    saveState.value = 'saved';
    if (wasNew) trackRouteCard(ROUTE_CARD_CREATED, eventContext());
    return data.saveRouteCard;
  } catch (error) {
    if (errorCode(error) === 'PRO_REQUIRED') {
      window.location.href = '/purchases';
      return null;
    }
    saveState.value = 'error';
    return null;
  }
}

function scheduleSave(): void {
  revision += 1;
  saveState.value = 'saving';
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => { void saveNow(); }, SAVE_DEBOUNCE_MS);
}

function setSetting<K extends keyof EditableSettings>(key: K, value: EditableSettings[K]): void {
  settings[key] = value;
  scheduleSave();
}

function setDate(value: string): void {
  setSetting('startDate', value || null);
}

function setStart(value: string): void {
  const minutes = parseClock(value);
  if (minutes !== null) setSetting('startMinutes', minutes);
}

function setGrid(value: string): void {
  setSetting('gridCrs', value || null);
}

function movePointTo(index: number, distance: number): void {
  points.value = movePoint(points.value, index, distance);
  selected.value = index;
  scheduleSave();
}

function patchPoint(index: number, patch: Parameters<typeof updatePoint>[2]): void {
  points.value = updatePoint(points.value, index, patch);
  scheduleSave();
}

function addPoint(): void {
  const { points: next, index } = addPointInSection(points.value, Math.max(selected.value - 1, 0));
  if (index < 0) return;
  points.value = next;
  selected.value = index;
  scheduleSave();
}

function removeSelected(index: number): void {
  points.value = removePoint(points.value, index);
  selected.value = Math.min(index, points.value.length - 1);
  scheduleSave();
}

async function startFrom(select: HTMLSelectElement): Promise<void> {
  const source = select.value;
  select.value = '';
  if (!source) return;
  busy.value = true;
  try {
    const data = await graphClient.request<{ routeCard: RouteCard }>(
      GetRouteCard, { hikeDayId: props.page.hikeDayId, startFrom: source },
    );
    points.value = fromCardPoints(data.routeCard.points);
    selected.value = 1;
    revision += 1;
    await saveNow();
  } catch {
    saveState.value = 'error';
  } finally {
    busy.value = false;
  }
}

/** Sharing and the PDF both need the card on the server, so an unsaved one is saved first. */
async function ensureSaved(): Promise<RouteCard | null> {
  if (card.value?.id && !saveTimer) return card.value;
  return saveNow();
}

async function setSharing(shared: boolean): Promise<void> {
  busy.value = true;
  try {
    const saved = await ensureSaved();
    if (!saved?.id) return;
    const data = await graphClient.request<{ setRouteCardSharing: RouteCard }>(
      SetRouteCardSharing, { input: { id: saved.id, shared } },
    );
    apply(data.setRouteCardSharing, { keepLocalPoints: true });
  } catch {
    saveState.value = 'error';
  } finally {
    busy.value = false;
  }
}

async function copyShareLink(): Promise<void> {
  if (!card.value?.shareUrl) return;
  try {
    await navigator.clipboard.writeText(card.value.shareUrl);
    copied.value = true;
    setTimeout(() => { copied.value = false; }, 3000);
  } catch {
    // Clipboard blocked: the link is on the page to copy by hand.
    copied.value = false;
  }
}

async function shareLink(): Promise<void> {
  await setSharing(true);
  await copyShareLink();
}

function stopSharing(): void {
  void setSharing(false);
}

async function downloadPdf(): Promise<void> {
  if (props.page.mode === 'shared') {
    trackRouteCard(ROUTE_CARD_PDF_EXPORTED, eventContext());
    window.location.href = `/route-cards/shared/${props.page.shareToken}.pdf`;
    return;
  }
  busy.value = true;
  const saved = await ensureSaved();
  busy.value = false;
  if (!saved?.id) return;
  trackRouteCard(ROUTE_CARD_PDF_EXPORTED, eventContext());
  window.location.href = `/route-cards/days/${props.page.hikeDayId}.pdf`;
}

function switchDay(hikeDayId: string): void {
  const day = props.page.days.find((candidate) => candidate.id === hikeDayId);
  if (day) window.location.href = day.url;
}

onMounted(load);
</script>

<style lang="scss">
.rc {
  min-height: 100vh;
  background: #17181D;
  color: #FFFFFF;
  font-family: 'Montserrat', sans-serif;
  letter-spacing: 0.02em;
  padding: 24px 32px 40px;
  box-sizing: border-box;

  @media (max-width: 768px) { padding: 16px; }
}

.rc__page {
  max-width: 1376px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.rc__header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.rc__heading { display: flex; align-items: center; gap: 14px; min-width: 0; }
.rc__titles { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.rc__title-row { display: flex; align-items: center; gap: 10px; }
.rc__title { margin: 0; font-size: 24px; font-weight: 700; line-height: 1.1; color: #FFFFFF; }
.rc__subtitle { font-size: 13px; color: #ACB0BF; }

.rc__help {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ACB0BF;

  &:hover, &:focus-visible { color: #FFFFFF; background: #2D2F35; }
}

.rc__pro {
  align-self: flex-start;
  background: #2CC2E3;
  color: #17181D;
  font-size: 10px;
  font-weight: 800;
  padding: 4px 8px;
  border-radius: 100px;
}

.rc__round-button {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #2D2F35;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #FFFFFF;
  flex-shrink: 0;

  &:hover { color: #FFFFFF; background: #434343; }
}

.rc__actions { display: flex; flex-wrap: wrap; gap: 10px; }

.rc__pill {
  height: 44px;
  padding: 0 18px;
  border-radius: 100px;
  border: 1px solid #505259;
  background: transparent;
  color: #FFFFFF;
  font: 600 13px 'Montserrat', sans-serif;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  text-decoration: none;

  &:hover { color: #FFFFFF; border-color: #ACB0BF; }
  &:disabled { opacity: 0.6; cursor: default; }

  &--primary {
    border: none;
    background: #FDDF00;
    color: #17181D;
    font-weight: 700;

    &:hover { color: #17181D; background: #FFE933; }
  }
}

.rc__share-url {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-size: 12px;
  color: #ACB0BF;

  input {
    flex: 1 1 320px;
    height: 40px;
    border-radius: 100px;
    border: 1px solid #505259;
    background: #2D2F35;
    color: #FFFFFF;
    padding: 0 16px;
    font: 500 13px 'Montserrat', sans-serif;
  }
}

.rc__settings { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }

.rc__field {
  height: 44px;
  padding: 0 6px 0 16px;
  border-radius: 100px;
  border: 1px solid #505259;
  background: #2D2F35;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  margin: 0;

  > span { color: #ACB0BF; white-space: nowrap; }

  input, select {
    background: transparent;
    border: none;
    color: #FFFFFF;
    font: 500 13px 'Montserrat', sans-serif;
    height: 40px;
    padding: 0 8px 0 0;
    color-scheme: dark;

    &:focus { outline: none; }
  }

  &:focus-within { border-color: #FDDF00; }
  &--accent { border-color: #FDDF00; }
  &--narrow input { width: 48px; }
  &--wide input { width: 180px; }

  option { color: #17181D; }
}

.rc__body { display: flex; flex-wrap: wrap; gap: 20px; align-items: flex-start; }

.rc__map-panel {
  flex: 1 1 420px;
  min-width: 0;
  background: #22242A;
  border-radius: 12px;
  overflow: hidden;
}

.rc__card-panel {
  flex: 999 1 640px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.rc__panel-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid #2D2F35;
}

.rc__panel-title { margin: 0; font-size: 14px; font-weight: 700; color: #FFFFFF; }
.rc__panel-actions { display: flex; gap: 8px; flex-wrap: wrap; }

.rc__inline-select select,
.rc__small-button {
  height: 36px;
  padding: 0 14px;
  border-radius: 100px;
  border: 1px solid #505259;
  background: transparent;
  color: #FFFFFF;
  font: 600 12px 'Montserrat', sans-serif;
  cursor: pointer;
  color-scheme: dark;
}

.rc__small-button--danger { border-color: #DB2B35; color: #FF8A91; }

.rc__help { margin: 0; padding: 12px 16px; font-size: 12px; color: #ACB0BF; line-height: 1.5; }

.rc__editor {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px;
  margin: 0 16px 14px 82px;
  padding: 12px;
  border-radius: 8px;
  background: #2D2F35;

  @media (max-width: 768px) { margin-left: 16px; }
}

.rc__editor-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 11px;
  color: #ACB0BF;
  flex: 1 1 180px;
  margin: 0;

  &--narrow { flex: 0 1 140px; }

  input, select {
    height: 40px;
    border-radius: 100px;
    border: 1px solid #505259;
    background: #17181D;
    color: #FFFFFF;
    padding: 0 14px;
    font: 500 13px 'Montserrat', sans-serif;
    color-scheme: dark;

    &:focus { outline: none; border-color: #FDDF00; }
  }
}

.rc__totals {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 10px;
}

.rc__tile {
  background: #22242A;
  border-radius: 8px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  span { font-size: 11px; color: #ACB0BF; }
  strong { font-size: 18px; font-weight: 700; }
}

.rc__yellow { color: #FDDF00; }
.rc__green { color: #4BC263; }
.rc__orange { color: #F57700; }

.rc__footnote, .rc__message { margin: 0; font-size: 12px; color: #ACB0BF; line-height: 1.5; }
.rc__message { font-size: 14px; padding: 40px 0; }
.rc__link { color: #2CC2E3; }

.rc__status { margin: 0; font-size: 12px; color: #ACB0BF; }
.rc__status--error { color: #FF8A91; }

.rc__paywall { position: relative; }

.rc__paywall-blur {
  display: flex;
  flex-direction: column;
  gap: 10px;
  filter: blur(5px);
  opacity: 0.55;

  div { background: #22242A; border-radius: 8px; height: 92px; }
}

.rc__paywall-card {
  position: absolute;
  inset: 20px 0 auto;
  margin: 0 auto;
  max-width: 420px;
  background: #22242A;
  border-radius: 12px;
  box-shadow: 0 -6px 14px rgba(0, 0, 0, 0.25);
  padding: 22px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  h2 { margin: 0; font-size: 20px; font-weight: 700; color: #FFFFFF; }
  p { margin: 0; font-size: 13px; color: #ACB0BF; }
  ul { margin: 0; padding-left: 18px; font-size: 13px; line-height: 1.5; color: #D9D9D9; }
}
</style>
